import React, { useState, useRef } from 'react';
import documentApi from '../services/documentApi';
import './DocumentUpload.css';

const DocumentUpload = ({
  transactionType,
  transactionId,
  onUploadSuccess,
  onUploadError,
  multiple = false,
  maxFiles = 10,
  maxFileSize = 20 * 1024 * 1024, // 20MB
  allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.xls', '.xlsx']
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [description, setDescription] = useState('');

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File "${file.name}" exceeds maximum size of ${(maxFileSize / (1024 * 1024)).toFixed(0)}MB`;
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      return `File "${file.name}" is not an allowed type. Allowed types: ${allowedTypes.join(', ')}`;
    }

    return null;
  };

  const handleFileSelect = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    const errors = [];
    const validFiles = [];

    fileArray.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError('');
      if (multiple) {
        setFiles((prev) => [...prev, ...validFiles].slice(0, maxFiles));
      } else {
        setFiles(validFiles.slice(0, 1));
      }
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    if (!transactionType || !transactionId) {
      setError('Transaction type and ID are required');
      return;
    }

    setUploading(true);
    setError('');
    setProgress(0);

    try {
      if (files.length === 1) {
        // Single file upload
        const document = await documentApi.uploadDocument(
          transactionType,
          transactionId,
          files[0],
          description
        );
        setProgress(100);
        setFiles([]);
        setDescription('');
        if (onUploadSuccess) {
          onUploadSuccess([document]);
        }
      } else {
        // Multiple file upload
        const documents = await documentApi.uploadMultiple(
          transactionType,
          transactionId,
          files,
          description
        );
        setProgress(100);
        setFiles([]);
        setDescription('');
        if (onUploadSuccess) {
          onUploadSuccess(documents);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload document(s)';
      setError(errorMessage);
      if (onUploadError) {
        onUploadError(err);
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = '.' + fileName.split('.').pop().toLowerCase();
    if (['.jpg', '.jpeg', '.png'].includes(ext)) {
      return '🖼️';
    } else if (ext === '.pdf') {
      return '📄';
    } else if (['.xls', '.xlsx'].includes(ext)) {
      return '📊';
    }
    return '📎';
  };

  return (
    <div className="document-upload-container">
      <div className="document-upload-header">
        <h4>Upload Documents</h4>
        <p className="document-upload-hint">
          Supported formats: PDF, Images (JPG, PNG), Excel (XLS, XLSX). Max size: {(maxFileSize / (1024 * 1024)).toFixed(0)}MB
        </p>
      </div>

      {error && (
        <div className="document-upload-error">
          {error}
        </div>
      )}

      <div
        className={`document-upload-dropzone ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          onChange={handleFileInputChange}
          accept={allowedTypes.join(',')}
          style={{ display: 'none' }}
        />
        <div className="document-upload-dropzone-content">
          <div className="document-upload-icon">📎</div>
          <p>Drag and drop files here, or click to select</p>
          {multiple && <p className="document-upload-hint-small">You can select multiple files</p>}
        </div>
      </div>

      {files.length > 0 && (
        <div className="document-upload-files">
          <h5>Selected Files ({files.length})</h5>
          {files.map((file, index) => (
            <div key={index} className="document-upload-file-item">
              <span className="document-upload-file-icon">{getFileIcon(file.name)}</span>
              <div className="document-upload-file-info">
                <span className="document-upload-file-name">{file.name}</span>
                <span className="document-upload-file-size">{formatFileSize(file.size)}</span>
              </div>
              <button
                type="button"
                className="document-upload-remove-btn"
                onClick={() => removeFile(index)}
                disabled={uploading}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="document-upload-description">
        <label>Description (Optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a description for the document(s)..."
          rows={2}
          disabled={uploading}
        />
      </div>

      <div className="document-upload-actions">
        <button
          type="button"
          className="document-upload-btn"
          onClick={handleUpload}
          disabled={uploading || files.length === 0}
        >
          {uploading ? (
            <>
              <span className="document-upload-spinner">⏳</span>
              Uploading... {progress > 0 && `${progress}%`}
            </>
          ) : (
            `Upload ${files.length > 0 ? `${files.length} ` : ''}File${files.length !== 1 ? 's' : ''}`
          )}
        </button>
      </div>
    </div>
  );
};

export default DocumentUpload;
